// Shared formatting + color foundation for the FinOps v2 Monitoring components.
// Pure, dependency-free. Keeps every chart/table/tile visually consistent:
// tabular-number money/percent/byte formatting + a validated categorical palette
// (dataviz six-checks PASS, worst-adjacent CVD ΔE 47.9) with STABLE, entity-keyed
// color assignment — a series keeps its color when the group_by or top-N changes
// (color follows the entity, never its rank).

// ---- number / unit formatting ----

export function formatMoney(v, { compact = false, cents = true } = {}) {
  if (v == null || Number.isNaN(v)) return '—';
  const n = Number(v);
  const abs = Math.abs(n);
  if (compact && abs >= 1000) {
    if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    return `$${(n / 1000).toFixed(1)}k`;
  }
  return n.toLocaleString('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0,
  });
}

export function formatPct(v, digits = 1) {
  if (v == null || Number.isNaN(v)) return '—';
  return `${Number(v).toFixed(digits)}%`;
}

export function formatCores(v, digits = 2) {
  if (v == null || Number.isNaN(v)) return '—';
  return Number(v).toFixed(digits);
}

// bytes → GiB (1 decimal) by default; the API mostly hands us GB already, so this
// is for raw-byte values only.
export function formatBytesGiB(bytes, digits = 1) {
  if (bytes == null || Number.isNaN(bytes)) return '—';
  return `${(Number(bytes) / 1024 ** 3).toFixed(digits)} GiB`;
}

export function formatGB(gb, digits = 1) {
  if (gb == null || Number.isNaN(gb)) return '—';
  return `${Number(gb).toFixed(digits)} GB`;
}

export function formatInt(v) {
  if (v == null || Number.isNaN(v)) return '—';
  return Number(v).toLocaleString('en-US');
}

// Adaptive time-axis tick: hour labels for ≤24h, day+hour for the 1–3 day
// middle ground (pure day labels there produce 2–3 lonely ticks), day labels
// beyond.
export function formatTimeTick(iso, windowHours) {
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z');
  if (Number.isNaN(d.getTime())) return iso;
  if (windowHours != null && windowHours <= 24) {
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  }
  if (windowHours != null && windowHours <= 72) {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit' });
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function formatDateTime(iso) {
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ---- color ----

// Validated categorical ramp (light surface). Assigned to entities in a fixed
// order; a 9th+ entity folds into the neutral "Other" slot (the API already
// collapses the long tail into an "other" series).
export const CATEGORICAL = [
  '#2563eb', '#0d9488', '#d97706', '#7c3aed',
  '#db2777', '#65a30d', '#0891b2', '#dc2626',
];
export const OTHER_COLOR = '#94a3b8'; // slate-400 — the reserved long-tail neutral

// Reserved semantic colors (never used for a categorical series).
export const SEMANTIC = {
  spot: '#7c3aed',        // violet
  on_demand: '#2563eb',   // blue
  idle: '#cbd5e1',        // neutral (slate-300)
  anomaly: '#dc2626',     // red
  good: '#16a34a',
  warn: '#d97706',
};

// Stable color for a series key: 'other'/'__idle__' get reserved slots; spot/
// on_demand map to the semantic pair; everything else hashes into CATEGORICAL so
// the same namespace/workload keeps its hue across regroupings.
export function colorForKey(key) {
  if (key == null) return OTHER_COLOR;
  const k = String(key).toLowerCase();
  if (k === 'other') return OTHER_COLOR;
  if (k === '__idle__' || k === 'idle') return SEMANTIC.idle;
  if (k === 'spot') return SEMANTIC.spot;
  if (k === 'on_demand' || k === 'on-demand') return SEMANTIC.on_demand;
  let h = 0;
  for (let i = 0; i < k.length; i++) h = (h * 31 + k.charCodeAt(i)) >>> 0;
  return CATEGORICAL[h % CATEGORICAL.length];
}

// Assign colors to an ordered list of keys, de-conflicting hash collisions by
// walking to the next free categorical hue — so a single chart never shows two
// adjacent series in the same color. Reserved keys keep their fixed slot.
export function assignColors(keys) {
  const out = {};
  const used = new Set();
  for (const key of keys) {
    let c = colorForKey(key);
    const reserved = ['other', '__idle__', 'idle', 'spot', 'on_demand', 'on-demand']
      .includes(String(key).toLowerCase());
    if (!reserved && used.has(c)) {
      const start = CATEGORICAL.indexOf(c);
      for (let i = 1; i <= CATEGORICAL.length; i++) {
        const cand = CATEGORICAL[(start + i) % CATEGORICAL.length];
        if (!used.has(cand)) { c = cand; break; }
      }
    }
    out[key] = c;
    used.add(c);
  }
  return out;
}

// ---- CSV export (client-side, same idea as CostAllocationPanel) ----

export function downloadCsv(filename, columns, rows) {
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = columns.map((c) => esc(c.label)).join(',');
  const body = rows.map((r) => columns.map((c) => esc(
    typeof c.value === 'function' ? c.value(r) : r[c.key]
  )).join(',')).join('\n');
  const blob = new Blob([head + '\n' + body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}
